/**
 * rejection-log.ts — every blocked Zone API call gets recorded.
 *
 * When an agent attempts a write that violates the sandbox (writing to a
 * locked element, exceeding zone permissions, injecting a blocked tag),
 * the Zone API rejects it AND logs a row to the user's audit chain so the
 * attempt is visible in the state explorer.
 *
 * The log is fail-soft: if the network is down or the chain is offline,
 * we queue locally (IndexedDB) and flush on reconnect. The Pulse cannot
 * be silenced by losing connectivity.
 *
 * Sandbox boundary: this file runs in the BROWSER. It does not encrypt.
 * It does not sign. The audit-chain server-side row generation
 * (auditLog.ts rowHash) handles those. The browser only provides the
 * intent + zeqond stamp + evidence.
 */

/** Reason a Zone API call was rejected. Stable wire enum. */
export type RejectionReason =
  | "target_is_locked"           // target element has data-zeq-locked or descends from one
  | "target_is_not_a_zone"       // selector doesn't match any [data-zeq-zone]
  | "permission_denied"          // zone exists but doesn't grant this operation
  | "content_blocked"            // sanitiser stripped tags/attrs (logged with detail)
  | "containment_escape"         // style would have escaped containment (fixed/absolute/etc.)
  | "id_shadow_attempt"          // attempted to set id matching a locked element
  | "zone_creation_attempt"      // attempted to create a new data-zeq-zone from inside one
  | "unknown_component"          // attempted to mount a component outside the registry
  | "rate_limited"               // too many writes per zeqond — flow control
  | "invalid_input";             // input failed schema validation before sandbox check

/** Structured payload sent to the audit chain. */
export interface RejectionEntry {
  reason: RejectionReason;
  /** Human-readable description for the state explorer. */
  intent: string;
  /** Zone id the agent was trying to write into (if known). */
  zoneId?: string;
  /** Selector or element id of the rejection target. */
  target?: string;
  /** Tags/attrs the sanitiser stripped, if reason="content_blocked". */
  stripped?: { tags?: string[]; attrs?: string[] };
  /** Local Zeqond at rejection time. */
  zeqondLocal: number;
  /** Phase ∈ [0,1) at rejection time. */
  phaseLocal: number;
  /** Pulse package version. */
  pulseVersion: string;
}

/** Default config. Override at Pulse.init() time. */
export interface RejectionLogConfig {
  /** Where to POST the rejection. Default: "/api/chain/rejection-log". */
  endpoint: string;
  /** Whether to also write to console.warn. Default: true in dev, false in prod. */
  consoleWarn: boolean;
  /** Max queued rejections in localStorage before we start dropping oldest. */
  maxQueueSize: number;
}

const DEFAULT_CONFIG: RejectionLogConfig = {
  endpoint: "/api/chain/rejection-log",
  consoleWarn: true,
  maxQueueSize: 256,
};

const QUEUE_KEY = "zeq.pulse.rejection-queue";
const PULSE_VERSION = "1.0.0-week1";  // bumped each release; baked at bundle time

let config: RejectionLogConfig = { ...DEFAULT_CONFIG };

/**
 * Configure the rejection log. Called once at Pulse.init().
 * Subsequent calls overwrite. Defaults are usable without configuring.
 */
export function configureRejectionLog(overrides: Partial<RejectionLogConfig>): void {
  config = { ...DEFAULT_CONFIG, ...overrides };
}

/**
 * Compute the current local Zeqond + phase. Pure math — does NOT contact
 * the network. Once the ZeqondClock module ships in Week 2, this defers
 * to the canonical clock; for Week 1 we derive directly from Date.now().
 *
 * This MUST stay consistent with the formula used by
 * shared/api-core/src/lib/auditLog.ts so server- and client-stamped
 * rows can be correlated.
 *
 *   T_Z       = 0.777 s
 *   zeqond    = floor(unix_seconds / 0.777)
 *   phase     = (unix_seconds mod 0.777) / 0.777   ∈ [0,1)
 */
function localZeqondAndPhase(): { zeqond: number; phase: number } {
  const T_Z = 0.777;
  const t = Date.now() / 1000;
  const zeqond = Math.floor(t / T_Z);
  const phase = ((t / T_Z) - zeqond);
  return { zeqond, phase };
}

/**
 * Record one rejection. Synchronous, returns immediately. The actual POST
 * is fire-and-forget. If the POST fails (network down, server 5xx), the
 * entry is queued in localStorage and re-tried on next page load + on
 * `pulse:online` event.
 *
 * Callers (zone-api.ts) MUST call this BEFORE returning rejection to the
 * agent — never silently. Every rejection is part of the audit trail.
 */
export function logRejection(args: {
  reason: RejectionReason;
  intent: string;
  zoneId?: string;
  target?: string;
  stripped?: { tags?: string[]; attrs?: string[] };
}): void {
  const { zeqond, phase } = localZeqondAndPhase();
  const entry: RejectionEntry = {
    reason: args.reason,
    intent: args.intent,
    zoneId: args.zoneId,
    target: args.target,
    stripped: args.stripped,
    zeqondLocal: zeqond,
    phaseLocal: phase,
    pulseVersion: PULSE_VERSION,
  };

  if (config.consoleWarn) {
    // Ungreppable safety net for developers — never disabled in dev.
    // eslint-disable-next-line no-console
    console.warn("[pulse:sandbox] rejected →", args.reason, {
      intent: args.intent,
      zoneId: args.zoneId,
      target: args.target,
      stripped: args.stripped,
      zeqond,
      phase: phase.toFixed(4),
    });
  }

  // Best-effort POST — never throws.
  void postEntry(entry).catch(() => enqueue(entry));
}

async function postEntry(entry: RejectionEntry): Promise<void> {
  // We deliberately use `keepalive: true` so the POST survives unloads
  // (e.g. if the rejection happens during navigation). The body is small
  // enough (<5 KB) to fit in the keepalive budget.
  const res = await fetch(config.endpoint, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(entry),
    keepalive: true,
  });
  if (!res.ok) throw new Error(`rejection-log POST returned ${res.status}`);
}

function enqueue(entry: RejectionEntry): void {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const list: RejectionEntry[] = raw ? JSON.parse(raw) : [];
    list.push(entry);
    while (list.length > config.maxQueueSize) list.shift();
    localStorage.setItem(QUEUE_KEY, JSON.stringify(list));
  } catch {
    /* localStorage quota / parse error — give up silently. The console.warn
       above already preserved the developer-visible record. */
  }
}

/**
 * Drain queued rejections. Called by Pulse.init() at boot and on the
 * `pulse:online` event. Drops on success; keeps + retries on failure.
 */
export async function drainRejectionQueue(): Promise<void> {
  let raw: string | null;
  try { raw = localStorage.getItem(QUEUE_KEY); }
  catch { return; }
  if (!raw) return;
  let list: RejectionEntry[];
  try { list = JSON.parse(raw); } catch { localStorage.removeItem(QUEUE_KEY); return; }
  if (!Array.isArray(list) || list.length === 0) return;

  const remaining: RejectionEntry[] = [];
  for (const entry of list) {
    try { await postEntry(entry); }
    catch { remaining.push(entry); }
  }
  if (remaining.length === 0) localStorage.removeItem(QUEUE_KEY);
  else localStorage.setItem(QUEUE_KEY, JSON.stringify(remaining));
}

/**
 * Internal — exposed for tests only. Returns a snapshot of the queue
 * without draining it.
 */
export function _peekQueue(): RejectionEntry[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

/**
 * Internal — exposed for tests only. Clears the queue.
 */
export function _clearQueue(): void {
  try { localStorage.removeItem(QUEUE_KEY); } catch { /* */ }
}
